import { useVenues } from "@/hooks/use-venues";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Plus, Store, X } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_app/shopping/new")({
  component: ShoppingNewPage,
});

function ShoppingNewPage() {
  const navigate = useNavigate();
  const { data: venuesResponse, isLoading } = useVenues();
  const suggestedVenues = venuesResponse?.data;
  const [selectedVenues, setSelectedVenues] = useState<string[]>([]);
  const [customVenue, setCustomVenue] = useState("");
  const [sessionName, setSessionName] = useState("");

  const toggleVenue = (venue: string) => {
    setSelectedVenues((prev) =>
      prev.includes(venue) ? prev.filter((v) => v !== venue) : [...prev, venue],
    );
  };

  const addCustomVenue = () => {
    const trimmed = customVenue.trim();
    if (trimmed && !selectedVenues.includes(trimmed)) {
      setSelectedVenues((prev) => [...prev, trimmed]);
      setCustomVenue("");
    }
  };

  const handleNext = () => {
    if (selectedVenues.length === 0) return;
    const trimmedName = sessionName.trim();
    navigate({
      to: "/shopping/pick",
      search: {
        venues: selectedVenues,
        ...(trimmedName ? { name: trimmedName } : {}),
      },
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-center gap-3 border-b px-4 py-3">
        <button
          type="button"
          onClick={() => navigate({ to: "/shopping" })}
          className="rounded-full p-1 text-muted-foreground hover:bg-muted"
          aria-label="戻る"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-bold text-foreground">どこに行く？</h1>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {/* セッション名入力 */}
        <div className="mb-4">
          <input
            type="text"
            value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
            placeholder="リスト名（省略可）"
            maxLength={50}
            className="w-full rounded-lg border border-border bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground"
          />
        </div>

        {isLoading ? (
          <p className="text-center text-muted-foreground">読み込み中...</p>
        ) : (
          <>
            {suggestedVenues && suggestedVenues.length > 0 && (
              <ul className="space-y-2">
                {suggestedVenues.map((venue) => (
                  <li key={venue}>
                    <button
                      type="button"
                      onClick={() => toggleVenue(venue)}
                      className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
                        selectedVenues.includes(venue)
                          ? "border-primary bg-primary/10 text-foreground"
                          : "border-border text-foreground hover:bg-muted"
                      }`}
                    >
                      <Store className="h-5 w-5 shrink-0" />
                      <span className="flex-1">{venue}</span>
                      {selectedVenues.includes(venue) && (
                        <X className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Selected custom venues not in suggestedVenues */}
            {selectedVenues
              .filter((v) => !suggestedVenues?.includes(v))
              .map((venue) => (
                <button
                  key={venue}
                  type="button"
                  onClick={() => toggleVenue(venue)}
                  className="mt-2 flex w-full items-center gap-3 rounded-lg border border-primary bg-primary/10 px-4 py-3 text-left text-foreground"
                >
                  <Store className="h-5 w-5 shrink-0" />
                  <span className="flex-1">{venue}</span>
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}

            {/* Custom venue input */}
            <div className="mt-4 flex gap-2">
              <input
                type="text"
                value={customVenue}
                onChange={(e) => setCustomVenue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCustomVenue();
                  }
                }}
                placeholder="その他のお店を追加..."
                className="flex-1 rounded-lg border border-border bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground"
              />
              <button
                type="button"
                onClick={addCustomVenue}
                disabled={!customVenue.trim()}
                className="rounded-lg bg-primary p-3 text-primary-foreground disabled:opacity-50"
                aria-label="追加"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
          </>
        )}
      </div>

      <div className="border-t px-4 py-3">
        <button
          type="button"
          onClick={handleNext}
          disabled={selectedVenues.length === 0}
          className="w-full rounded-full bg-primary py-3 text-base font-medium text-primary-foreground disabled:opacity-50"
        >
          次へ（{selectedVenues.length}店）
        </button>
      </div>
    </div>
  );
}
